// SignatureModal.jsx
import React, { useRef, useState, useEffect } from "react";
import styles from "../css/CreateCoursePage.module.css"; // re-use same module so styles live together
import PropTypes from "prop-types";

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSave: (dataUrl:string, file: File|null) => void
 */
const SignatureModal = ({ onClose, onSave }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fileRef = useRef(null);
  const [tab, setTab] = useState("draw"); // 'draw' | 'upload'
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [uploadedDataUrl, setUploadedDataUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // high DPI support
    const dpr = window.devicePixelRatio || 1;
    const rect = containerRef.current.getBoundingClientRect();
    canvas.width = Math.max(600, rect.width) * dpr;
    canvas.height = 300 * dpr;
    canvas.style.width = "100%";
    canvas.style.height = "300px";
    ctx.scale(dpr, dpr);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = "#000";
    clearCanvas(); // make sure background is light
    // eslint-disable-next-line
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // fill with transparent white-ish background similar to screenshot
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Optionally draw a faint background - leaving transparent for now
  };

  const getPointer = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (tab !== "draw") return;
    setIsDrawing(true);
    const pos = getPointer(e);
    setLastPos(pos);
  };

  const draw = (e) => {
    if (!isDrawing || tab !== "draw") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPointer(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setLastPos(pos);
  };

  const stop = () => {
    setIsDrawing(false);
  };

  const handleUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadedDataUrl(ev.target.result);
      setUploadedFile(file);
    };
    reader.readAsDataURL(file);
  };

  const onFileInputChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    handleUpload(f);
  };

  const handleSave = () => {
    if (tab === "upload" && uploadedDataUrl) {
      // send uploaded image
      onSave(uploadedDataUrl, uploadedFile);
      onClose();
      return;
    }
    // draw tab -> extract canvas dataUrl
    const canvas = canvasRef.current;
    if (!canvas) return;
    // small white background so signature isn't transparent (optional)
    const tmp = document.createElement("canvas");
    tmp.width = canvas.width;
    tmp.height = canvas.height;
    const tctx = tmp.getContext("2d");
    // fill white background
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, tmp.width, tmp.height);
    // draw existing canvas (note: original canvas has been scaled for DPR)
    tctx.drawImage(canvas, 0, 0);
    const dataUrl = tmp.toDataURL("image/png");
    // convert to file for uploading if needed
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    const file = new File([u8], "signature.png", { type: mime });
    onSave(dataUrl, file);
    onClose();
  };

  const handleClear = () => {
    clearCanvas();
    setUploadedDataUrl(null);
    setUploadedFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div
      className={styles.signatureModalCardWrap}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, borderBottom: "1px solid #eee", paddingBottom: 12}}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26 }}>
            <img src="/Image/plus.svg" alt="plus" style={{ display: "block", width: "16px", height: "16px" }} />
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, fontFamily:"Lato" }}>Signature</div>
        </div>
        <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#f33070" }}>✕</button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <button
          className={`${styles.sigTabBtn} ${tab === "draw" ? styles.sigTabActive : ""}`}
          onClick={() => setTab("draw")}
        >
          Draw
        </button>
        <button
          className={`${styles.sigTabBtn} ${tab === "upload" ? styles.sigTabActive : ""}`}
          onClick={() => setTab("upload")}
        >
          Upload
        </button>
      </div>

      <div ref={containerRef} className={styles.signatureCanvasWrap}>
        {tab === "draw" && (
          <canvas
            ref={canvasRef}
            className={styles.signatureCanvas}
            onMouseDown={start}
            onMouseMove={draw}
            onMouseUp={stop}
            onMouseLeave={stop}
            onTouchStart={start}
            onTouchMove={draw}
            onTouchEnd={stop}
            aria-label="Signature drawing area"
          />
        )}

        {tab === "upload" && (
          <div className={styles.uploadArea}>
            <input
              ref={fileRef}
              type="file"
              accept="image/png, image/jpeg"
              onChange={onFileInputChange}
              style={{ display: "none" }}
            />
            <div className={styles.uploadPreview}>
              {uploadedDataUrl ? (
                <img src={uploadedDataUrl} alt="uploaded signature" style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain" }} />
              ) : (
                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={() => fileRef.current.click()}
                    className={styles.sigSelectButton}
                    type="button"
                  >
                    Select Image
                  </button>
                  <p style={{ marginTop: 8, color: "#888" }}>Support jpeg and png files</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button
          onClick={handleClear}
          className={styles.sigSecondaryBtn}
          type="button"
        >
          CLEAR
        </button>

        <button
          onClick={() => onClose()}
          className={styles.sigSecondaryBtn}
          type="button"
        >
          CANCEL
        </button>

        <button
          onClick={handleSave}
          className={styles.sigPrimaryBtn}
          type="button"
        >
          SAVE
        </button>
      </div>
    </div>
  );
};

SignatureModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default SignatureModal;
